import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { ApiResponse, OverTimeRequestApiRow, OverTimeRequestListResponse, OverTimeRequestVm, OverTimeStatusLookupItem } from '@/api/types'

function log(label: string, data: Record<string, unknown>) {
  try {
    console.groupCollapsed(label)
    for (const [k, v] of Object.entries(data)) console.log(k, v)
    console.groupEnd()
  } catch {
    // ignore
  }
}

function unwrap(payload: any) {
  if (payload && typeof payload === 'object') {
    if ('success' in payload && 'data' in payload) return payload.data
    if ('data' in payload) return payload.data
  }
  return payload
}

function toArray(payload: any): any[] {
  const raw = unwrap(payload)
  if (Array.isArray(raw)) return raw
  if (raw && typeof raw === 'object') {
    if (Array.isArray((raw as any).data)) return (raw as any).data
    if (Array.isArray((raw as any).items)) return (raw as any).items
    if (Array.isArray((raw as any).result)) return (raw as any).result
  }
  if (Array.isArray(payload?.data)) return payload.data
  return []
}

function num(v: any, fallback = 0) {
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function str(v: any) {
  return typeof v === 'string' ? v : String(v ?? '')
}

function mapRow(raw: any): OverTimeRequestApiRow {
  return {
    id: num(raw?.id ?? raw?.Id),
    empId: raw?.empId ?? raw?.EmpId,
    overTimeDate: raw?.overTimeDate ?? raw?.OverTimeDate ?? raw?.overTimeDateTime ?? raw?.OverTimeDateTime,
    timeFrom: raw?.timeFrom ?? raw?.TimeFrom,
    timeTo: raw?.timeTo ?? raw?.TimeTo,
    totalHours: raw?.totalHours ?? raw?.TotalHours,
    amount: raw?.amount ?? raw?.Amount,
    details: raw?.details ?? raw?.Details,
    status: raw?.status ?? raw?.Status,
    approvalDate: raw?.approvalDate ?? raw?.ApprovalDate,
    isEditable: raw?.isEditable ?? raw?.IsEditable,
    branchId: raw?.branchId ?? raw?.BranchId,
  }
}

function normalizeVm(raw: any, branchId: number): OverTimeRequestVm {
  return {
    id: num(raw?.id ?? raw?.Id),
    empId: num(raw?.empId ?? raw?.EmpId),
    overTimeDate: str(raw?.overTimeDate ?? raw?.OverTimeDate),
    timeFrom: str(raw?.timeFrom ?? raw?.TimeFrom),
    timeTo: str(raw?.timeTo ?? raw?.TimeTo),
    totalHours: str(raw?.totalHours ?? raw?.TotalHours),
    amount: num(raw?.amount ?? raw?.Amount),
    details: str(raw?.details ?? raw?.Details),
    status: num(raw?.status ?? raw?.Status),
    approvalDate: str(raw?.approvalDate ?? raw?.ApprovalDate),
    isEditable: num(raw?.isEditable ?? raw?.IsEditable),
    branchId: num(raw?.branchId ?? raw?.BranchId, branchId),
  }
}

function mapStatusLookup(raw: any): OverTimeStatusLookupItem {
  return {
    id: num(raw?.id ?? raw?.Id),
    lookupCodeId: raw?.lookupCodeId ?? raw?.LookupCodeId,
    lookupNameEn: raw?.lookupNameEn ?? raw?.LookupNameEn,
    lookupNameAr: raw?.lookupNameAr ?? raw?.LookupNameAr,
    name: raw?.name ?? raw?.Name,
    nameEn: raw?.nameEn ?? raw?.NameEn,
    nameAr: raw?.nameAr ?? raw?.NameAr,
    codeType: raw?.codeType ?? raw?.CodeType,
  }
}

export class OverTimeRequestApiService {
  async getAll(branchId: number, pageNumber: number, pageSize: number, codeType: number): Promise<OverTimeRequestListResponse> {
    const url = endpoints.overTimeRequest.getAll(branchId, pageNumber, pageSize, codeType)
    const response = await apiClient.get<ApiResponse<any> | any>(url, { skipHandledLocallyErrorUI: true } as any)
    const payload = response.data
    const raw = unwrap(payload)
    const list = toArray(raw).map(mapRow)
    const totalRecords = num(raw?.totalRecords ?? payload?.totalRecords ?? raw?.totalCount ?? payload?.totalCount, list.length)
    return { totalRecords, pageNumber, pageSize, data: list }
  }

  async getById(id: number, branchId: number): Promise<OverTimeRequestVm | null> {
    const url = endpoints.overTimeRequest.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<any> | any>(url, { skipHandledLocallyErrorUI: true } as any)
    const payload = response.data
    const raw = unwrap(payload)
    if (!raw) return null
    const source = Array.isArray(raw) ? raw[0] : Array.isArray(raw?.data) ? raw.data[0] : raw
    if (!source || typeof source !== 'object') return null
    return normalizeVm(source, branchId)
  }

  async getStatusLookups(): Promise<OverTimeStatusLookupItem[]> {
    const url = endpoints.overTimeRequest.getStatusLookups
    const response = await apiClient.get<ApiResponse<any> | any>(url, { skipHandledLocallyErrorUI: true } as any)
    return toArray(response.data).map(mapStatusLookup).filter((x) => x.id > 0)
  }

  async create(vm: OverTimeRequestVm): Promise<any> {
    const url = endpoints.overTimeRequest.create
    log('[OverTimeRequestApi.create] Request', { url, payload: vm })
    try {
      const response = await apiClient.post(url, vm, { skipHandledLocallyErrorUI: true } as any)
      log('[OverTimeRequestApi.create] Response', { url, status: response.status, response: response.data })
      return response.data
    } catch (e: any) {
      log('[OverTimeRequestApi.create] Error', {
        url,
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
        headers: e?.response?.headers,
      })
      throw e
    }
  }

  async update(vm: OverTimeRequestVm): Promise<any> {
    const url = endpoints.overTimeRequest.update
    log('[OverTimeRequestApi.update] Request', { url, payload: vm })
    try {
      const response = await apiClient.put(url, vm, { skipHandledLocallyErrorUI: true } as any)
      log('[OverTimeRequestApi.update] Response', { url, status: response.status, response: response.data })
      return response.data
    } catch (e: any) {
      log('[OverTimeRequestApi.update] Error', {
        url,
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
        headers: e?.response?.headers,
      })
      throw e
    }
  }

  async delete(id: number): Promise<any> {
    const url = endpoints.overTimeRequest.delete(id)
    log('[OverTimeRequestApi.delete] Request', { url, id })
    try {
      const response = await apiClient.delete(url, { skipHandledLocallyErrorUI: true } as any)
      log('[OverTimeRequestApi.delete] Response', { url, status: response.status, response: response.data })
      return response.data
    } catch (e: any) {
      log('[OverTimeRequestApi.delete] Error', {
        url,
        message: e?.message,
        status: e?.response?.status,
        data: e?.response?.data,
        headers: e?.response?.headers,
      })
      throw e
    }
  }
}

export const overTimeRequestApiService = new OverTimeRequestApiService()

